import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { urlWithBasePath } from '~/utils/url-helpers';
import { Heading5 } from '~/theme/headings';
import {
    RootContainer,
    ServiceDescriptionContainer,
    CardsContainer,
    CardContainer,
    ServicesAvailableImage,
    ServiceHeading,
} from './styles';
import { CALLOUT_TYPE, Flow, FormBuilderAppUrlMap, FormBuilderAppType } from '~/constants';
import Callout from '~/components/common/callout';
import { TextParagraph } from '~/theme';
import Link from '~/components/common/link';
import { LeadParagraph } from '~/theme/elements';
import getRuntimeConfig from '~/services/runtime-config.service';
import { displayIntegratedApp, getFormBuilderAppUrl } from '~/utils/utils';
import { NewBadge } from '~/components/splash/services-available/styles';
import { RuntimeConfig } from '~/models/runtime-config';

const servicesAvailable = [
    {
        id: 'driverAndVehicle',
        title: 'servicesAvailable.driverAndVehicle',
        imageUrl: urlWithBasePath('/images/splash-driver-and-vehicle.png'),
        imgAlt: 'servicesAvailable.driverAndVehicleAlt',
        links: [
            {
                id: 'payFines',
                text: 'servicesAvailable.payFines',
                url: 'servicesAvailable.payFinesUrl',
            },
            {
                id: 'renewLicencePlate',
                text: 'servicesAvailable.renewLicencePlate',
                url: 'servicesAvailable.renewLicencePlateUrl',
            },
            {
                id: 'renewDriverLicence',
                text: 'servicesAvailable.renewDriverLicence',
                url: 'servicesAvailable.renewDriverLicenceUrl',
            },
            {
                id: 'appRenewal',
                text: 'servicesAvailable.appRenewal',
                url: 'servicesAvailable.appRenewalUrl',
            },
            {
                id: 'plpOrder',
                text: 'servicesAvailable.plpOrder',
                url: 'servicesAvailable.plpOrderUrl',
            },
            {
                id: 'glpOrder',
                text: 'servicesAvailable.glpOrder',
                url: 'servicesAvailable.glpOrderUrl',
            },
            {
                id: 'opcRenewal',
                text: 'servicesAvailable.opcRenewal',
                url: 'servicesAvailable.opcRenewalUrl',
            },
            {
                id: 'driverRecord3Year',
                text: 'servicesAvailable.dr3Order',
                url: 'servicesAvailable.dr3OrderUrl',
            },
            {
                id: 'vehicleAbstractUncertified',
                text: 'servicesAvailable.vauOrder',
                url: 'servicesAvailable.vauOrderUrl',
            },
            {
                id: 'specialPermit',
                text: 'servicesAvailable.spOrder',
                url: 'servicesAvailable.spOrderUrl',
            },
        ],
    },
    {
        id: 'health',
        title: 'servicesAvailable.health',
        imageUrl: urlWithBasePath('/images/splash-health.png'),
        imgAlt: 'servicesAvailable.healthAlt',
        links: [
            {
                id: 'renewHealthCard',
                text: 'servicesAvailable.renewHealthCard',
                url: 'servicesAvailable.renewHealthCardUrl',
            },
            {
                id: 'renewChildHealthCard',
                text: 'servicesAvailable.renewChildHealthCard',
                url: 'servicesAvailable.renewChildHealthCardUrl',
            },
            // {
            //     id: Flow.CHC_TRACK,
            //     text: 'servicesAvailable.childHealthCardRenewalStatus',
            //     url: 'servicesSection.chcStatusUrl',
            //     openInNewTab: true,
            // },
            {
                id: Flow.EHOCM,
                text: 'servicesAvailable.fileComplaint',
                url: 'servicesAvailable.fileComplaintUrl',
                openInNewTab: false,
                // hasNewBadge: true,
            },
            {
                id: Flow.ODR,
                text: 'servicesAvailable.organDonor',
                url: 'servicesAvailable.organDonorUrl',
                openInNewTab: false,
            },
            {
                id: 'healthySmilesOntarioProgram',
                text: 'servicesAvailable.healthySmilesOntarioProgram',
                url: 'servicesAvailable.healthySmilesOntarioProgramUrl',
                openInNewTab: false,
                hasNewBadge: true,
            },
        ],
    },
    {
        id: 'identification',
        title: 'servicesAvailable.lifeEvents',
        imageUrl: urlWithBasePath('/images/splash-identification.png'),
        imgAlt: 'servicesAvailable.lifeEvents',
        links: [
            {
                id: 'changeAddress',
                text: 'servicesAvailable.changeAddress',
                url: 'servicesAvailable.changeAddressUrl',
            },
            {
                id: Flow.VECON,
                text: 'servicesAvailable.changeName',
                url: 'servicesAvailable.changeNameUrl',
                openInNewTab: false,
            },
        ],
    },
    {
        id: 'labour',
        title: 'servicesAvailable.labour',
        imageUrl: urlWithBasePath('/images/Labour.png'),
        imgAlt: 'servicesAvailable.labour',
        links: [
            {
                id: 'oer',
                text: 'servicesAvailable.oer',
                url: 'servicesAvailable.oerUrl',
            },
        ],
    },
];

export default function ServicesAvailable() {
    const { t, i18n } = useTranslation('splash');
    const [config, setConfig] = useState<RuntimeConfig>();

    useEffect(() => {
        getRuntimeConfig().then(config => setConfig(config));
    }, []);

    const getServiceUrl = (link: { id: string; text: string; url: string | Flow }) => {
        const isFormBuilderApp = !!FormBuilderAppUrlMap[link.id as FormBuilderAppType];
        if (!isFormBuilderApp) {
            return t(link.url);
        }

        const translatedUrl = t(link.url);
        if (translatedUrl?.startsWith('https')) {
            return translatedUrl;
        }

        return getFormBuilderAppUrl(
            link.id as FormBuilderAppType,
            config?.cxpWebHost as string,
            i18n.language as 'en' | 'fr'
        );
    };

    return (
        <>
            {config?.environment && (
                <RootContainer>
                    <ServiceHeading>{t('servicesAvailable.heading')}</ServiceHeading>
                    <CardsContainer>
                        {servicesAvailable.map(item => (
                            <CardContainer key={item.id}>
                                <ServicesAvailableImage
                                    height={195}
                                    width={260}
                                    src={item.imageUrl}
                                    alt={t(item.imgAlt) ?? ''}
                                />
                                <ServiceDescriptionContainer>
                                    <Heading5>{t(item.title)}</Heading5>
                                    {item.links.map(link => {
                                        const formBuilderApp =
                                            FormBuilderAppUrlMap[link.id as FormBuilderAppType];
                                        if (
                                            !config?.environment ||
                                            (formBuilderApp &&
                                                !displayIntegratedApp(link.id, config?.environment))
                                        ) {
                                            return null;
                                        }

                                        const shouldOpenInNewWindow =
                                            !formBuilderApp && (link.openInNewTab ?? true);

                                        const handleClick =
                                            link.id === Flow.VECON
                                                ? () => {
                                                      Object.keys(sessionStorage)
                                                          .filter(key =>
                                                              key.toUpperCase().includes(Flow.VECON)
                                                          )
                                                          .forEach(key =>
                                                              sessionStorage.removeItem(key)
                                                          );
                                                  }
                                                : undefined;

                                        return (
                                            <TextParagraph key={`${item.id}-${link.id}`}>
                                                <Link
                                                    href={getServiceUrl(link)}
                                                    openInNewWindow={shouldOpenInNewWindow}
                                                    onClick={handleClick}
                                                >
                                                    {t(link.text)}
                                                </Link>
                                                {link.hasNewBadge && (
                                                    <NewBadge>
                                                        {t('servicesAvailable.new')}
                                                    </NewBadge>
                                                )}
                                            </TextParagraph>
                                        );
                                    })}
                                </ServiceDescriptionContainer>
                            </CardContainer>
                        ))}
                    </CardsContainer>
                    <Callout calloutType={CALLOUT_TYPE.CALLOUT}>
                        <LeadParagraph className="leadText">
                            {t('servicesAvailable.calloutHeading')}
                        </LeadParagraph>
                        <TextParagraph>
                            {t('servicesAvailable.calloutLine1')}
                            {` `}
                            <Link href={t('common:serviceOntarioPage') ?? '#'} openInNewWindow>
                                {t('servicesAvailable.serviceOntario')}
                            </Link>
                        </TextParagraph>
                    </Callout>
                </RootContainer>
            )}
        </>
    );
}
